// src/components/healthrecords/useAutoTriage.js
//
// Auto-triage pipeline hook.
// Called after a BLOOD_TEST PDF is uploaded and text extraction is complete.
//
// Pipeline:
//   1. Extract PDF text client-side (pdf.js)
//   2. PATCH extracted text to backend → status: EXTRACTING → EXTRACTED
//   3. POST to /api/triage/cardiac (Claude AI)
//   4. Random Forest — DISABLED (Sep 2026, not performing well). Step 4 used
//      to POST to /api/rf-score here; that call is skipped below, not just
//      pointed at a disabled backend, so triage doesn't pay for a round trip
//      whose answer is always null. The backend endpoint is also disabled
//      (RfScoringController) for any other caller still hitting it.
//   5. POST triage result to /api/health-records/{id}/triage → status: TRIAGED
//   6. Return updated record with triage summary
//
// RF model moved server-side (Sprint 10) for IP protection + version control.

import { useState, useCallback, useRef, useEffect, useSyncExternalStore } from 'react';
// RFModel.js removed — RF scoring is fully server-side via POST /api/rf-score
import { healthRecordAPI } from '../../services/api';
// `api` is the default axios instance export — same file, used for /triage/cardiac
// which is not part of healthRecordAPI (different controller on backend)
import api from '../../services/api';
import { extractPdfText } from '../../utils/pdfText';
import { isScanImage } from '../../utils/scanImage';
import { buildActionPlan, serializeActionPlan } from '../../utils/triageUtils';

export { isScanImage };

// ── Lab text for Claude ───────────────────────────────────────────────────────
// Only the report's own text is sent. The patient's name, age, gender and blood type used to be prepended
// here, but the backend already resolves age and gender from patientId, no prompt uses the name or blood
// type, and the less identifying text leaves the app the less the server-side redaction has to catch.

// ── Pipeline stages (for progress display) ─────────────────────────────────────

export const TRIAGE_STAGES = {
  IDLE:        { label: '',                          step: 0 },
  EXTRACTING:  { label: 'Extracting PDF text…',      step: 1 },
  SAVING_TEXT: { label: 'Saving extracted text…',    step: 2 },
  SCAN_READING:{ label: 'Reading scanned report…',   step: 2 },
  CLAUDE:      { label: 'Running AI models…',        step: 3 },
  RF:          { label: 'Finalizing…',    step: 4 },
  SAVING:      { label: 'Saving triage results…',    step: 5 },
  DONE:        { label: 'Triage complete',           step: 6 },
  // The server holds the report for a navigator (a scan that is hard to read, or a report with several dated columns): not a failure,
  // and not complete either. The record shows why, and the patient sees it as "being checked by your navigator".
  HELD:        { label: 'Being checked by your navigator', step: 6 },
  FAILED:      { label: 'Triage failed',             step: -1 },
  SKIPPED:     { label: 'Not a blood test PDF',      step: -2 },
  // DEF-023 follow-up: the report's own printed name/age disagrees with the
  // patient's profile (see IdentityMismatchException on the backend). The
  // pipeline pauses HERE — not FAILED — pending the caller rendering a
  // confirmation prompt and calling confirmIdentityMismatch()/cancelIdentityMismatch().
  AWAITING_IDENTITY_CONFIRMATION: { label: 'Waiting for confirmation…', step: -3 },
  CANCELLED:   { label: 'Upload cancelled',          step: -4 },
};

// ── In-flight run (survives the tab unmounting) ────────────────────────────────
// The pipeline keeps running in the background when the navigator switches away from the Health Records tab, but the hook's own
// state is thrown away with the component. This module-level snapshot lets a freshly mounted tab pick the run back up and show
// its progress bar. Only active stages are kept: once a run settles, the record list is the source of truth.
let inflight = null; // { recordId, stage }
const inflightListeners = new Set();
const ACTIVE_STAGES = ['EXTRACTING', 'SAVING_TEXT', 'SCAN_READING', 'CLAUDE', 'RF', 'SAVING'];

function setInflight(next) {
  inflight = next;
  inflightListeners.forEach(l => l());
}
function subscribeInflight(l) {
  inflightListeners.add(l);
  return () => inflightListeners.delete(l);
}

/** The run currently in flight anywhere in the app, as { recordId, stage }, or null. */
export function useInflightTriage() {
  return useSyncExternalStore(subscribeInflight, () => inflight);
}

// ── Hook ────────────────────────────────────────────────────────────────────────

/**
 * Extracts RF feature values from Claude's marker array.
 * Replaces parseMarkersForRF from RFModel.js — that file is no longer imported.
 * Only core RF features extracted here; extended panel (TSH, VitD etc.) sent as-is.
 */
function extractRfFeatures(claudeMarkers) {
  const result = {};
  if (!claudeMarkers) return result;
  claudeMarkers.forEach(m => {
    const val  = parseFloat(m.value);
    if (isNaN(val)) return;
    const name = (m.name ?? '').toLowerCase();
    if (name.includes('ldl'))                                      result.ldl           = val;
    else if (name.includes('hdl'))                                 result.hdl           = val;
    else if (name.includes('trig'))                                result.triglycerides = val;
    else if (name.includes('hs-crp') || name.includes('hscrp') || (name.includes('crp') && name.includes('sens'))) result.hsCrp = val;
    else if (name.includes('crp') && !name.includes('micro'))      result.hsCrp         = val;
    else if (name.includes('hba1c') || name.includes('glycated')) result.hba1c          = val;
    else if (name.includes('egfr'))                                result.egfr           = val;
    else if (name.includes('troponin'))                            result.troponin       = val;
    else if (name.includes('ck-mb') || name.includes('ckmb'))     result.ckmb           = val;
    else if (name.includes('bnp'))                                 result.bnp            = val;
    else if (name.includes('homocyst'))                            result.homocysteine   = val;
    else if (name.includes('fasting glucose') || name.includes('fbs') || name.includes('fbg')) result.fastingGlucose = val;
    else if (name.includes('tsh'))                                 result.tsh            = val;
    else if (name.includes('vitamin d') || name.includes('25-oh')) result.vitD           = val;
    else if (name.includes('b12') || name.includes('cobalamin'))  result.b12             = val;
    else if ((name.includes('haemoglobin') || name.includes('hemoglobin')) && !name.includes('hba1c')) result.haemoglobin = val;
  });
  return result;
}

export function useAutoTriage() {
  const [stage, setStage]     = useState('IDLE');
  const [triageError, setTriageError] = useState(null);
  // DEF-023 follow-up: { extractedName, extractedAge, profileName, profileAge }
  // from the backend's 409 IDENTITY_MISMATCH response, or null when there's
  // nothing to confirm. Callers render a modal off this while stage is
  // AWAITING_IDENTITY_CONFIRMATION.
  const [identityMismatch, setIdentityMismatch] = useState(null);
  // isMounted guard — prevents setState after component unmounts
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Record this hook's current run belongs to — the key for the in-flight snapshot above.
  const runRecordIdRef = useRef(null);

  const safeSetStage = useCallback((s) => {
    if (isMountedRef.current) setStage(s);
    // Deliberately not guarded by isMountedRef: this is what a remounted tab reads.
    const recordId = runRecordIdRef.current;
    if (recordId) {
      if (ACTIVE_STAGES.includes(s)) setInflight({ recordId, stage: s });
      else if (inflight?.recordId === recordId) setInflight(null);
    }
  }, []);
  const safeSetError = useCallback((e) => {
    if (isMountedRef.current) setTriageError(e);
  }, []);

  // Stashed continuation state for the pause-for-confirmation flow — enough
  // to both retry the extracted-text save (with confirmation) and carry on
  // into Claude/RF/save exactly where the pipeline left off. Cleared on
  // confirm, cancel, or a fresh runAutoTriage() call.
  const pendingRef = useRef(null);

  /**
   * runAutoTriage — call this after upload OR to re-run triage on an existing record.
   *
   * @param {object} record           — the HealthRecordDTO.Response
   * @param {File|null} file          — original File object (upload path). Pass null for re-run.
   * @param {object} patient          — the patient object (for lab text context)
   * @param {UUID}   patientId        — patient UUID
   * @param {string} [preExtractedText] — already-extracted text (re-run path).
   *                                     When provided, Step 1 (PDF extraction) is skipped.
   * @param {object} [apiOverrides]   — 30 Jul 2026, Feature #3. Optional
   *                                    { saveExtractedText, saveTriage } functions,
   *                                    each with the same (patientId, recordId, ...)
   *                                    signature as healthRecordAPI's own. Defaults to
   *                                    healthRecordAPI — every existing caller (patient
   *                                    self-upload) is unaffected. The subscriber
   *                                    upload page passes subscriberAPI's equivalents
   *                                    instead, since PatientAccessGuard has no concept
   *                                    of a subscriber and healthRecordAPI's endpoints
   *                                    are gated by it.
   * @returns {object|null}           — triage result, or null if skipped/failed
   */
  /**
   * Steps 3-5 (Claude → RF → save triage) — the part of the pipeline that
   * runs identically whether the extracted-text save just succeeded
   * normally, or succeeded on a retry after the uploader confirmed an
   * identity mismatch. Extracted so confirmIdentityMismatch() below can
   * resume here without duplicating this logic.
   */
  const runClaudeRfAndSave = useCallback(async (record, patient, patientId, extractedText, saveTriageFn) => {
    // ── Step 3: Claude AI ──────────────────────────────────────────────────────
    safeSetStage('CLAUDE');
    const labText = extractedText;
    const claudeResponse = await api.post('/triage/cardiac', { labText, patientId });

    if (!claudeResponse.data.success) {
      throw new Error(claudeResponse.data.error || 'Claude triage failed');
    }

    const claudeResult = JSON.parse(
      claudeResponse.data.result.replace(/```json|```/g, '').trim()
    );

    // ── Step 4: Random Forest — DISABLED (Sep 2026, not performing well) ────
    // Used to POST /api/rf-score here; that endpoint now always answers with
    // a null verdict anyway (RfScoringController, backend), so the call is
    // skipped rather than made and ignored — rfResult is built locally in
    // the exact "did not run" shape the endpoint used to return, and
    // everything downstream (missingRf, consensusScore, triagePayload's rf*
    // fields) already handles that shape correctly, unchanged.
    //
    // Revert: restore the extractRfFeatures(...) call and the api.post to
    // '/rf-score', once the backend side (RfScoringController) is re-enabled
    // too — re-enabling only one side leaves the other silently discarding
    // or fabricating a verdict.
    safeSetStage('RF');
    const rfResult = { riskLevel: null, riskScore: null, confidence: null,
      treesUsed: 0, totalTrees: 0, missingKeys: [], featureFlags: [] };

    // ── Step 5: Save triage result ────────────────────────────────────────────
    safeSetStage('SAVING');

    // Build the action plan from this triage result so it is stored atomically.
    // Uses the same logic as ActionPlanTab in TriageReportModal — buildActionPlan()
    // is the single source of truth for both.
    const missingC   = claudeResult.missing_markers ?? [];
    const missingRf  = rfResult.missingKeys ?? [];
    const allMissing = [...new Set([
      ...missingC.map(m => m.toLowerCase()),
      ...missingRf.map(m => m.toLowerCase()),
    ])].map(m => missingC.find(x => x.toLowerCase() === m) || missingRf.find(x => x.toLowerCase() === m) || m);

    // consensusScore mirrors the backend: max(claude, rf)
    const consensusScore = Math.max(claudeResult.risk_score ?? 0, rfResult.riskScore ?? 0);
    const hasAcuteAlert  = !!(claudeResult.acute_alert_detail || consensusScore >= 85);

    // Build action plan — same shape ActionPlanTab renders
    const partialTriage = {
      hasAcuteAlert,
      acuteAlertDetail: claudeResult.acute_alert_detail || null,
      claudeRecommendation: claudeResult.recommendation || claudeResult.clinical_recommendation || null,
    };
    const actionPlan = buildActionPlan(partialTriage, allMissing);

    const triagePayload = {
      triggeredBy: 'AUTO_UPLOAD',

      // Claude AI output
      claudeRiskLevel:      claudeResult.risk_level,
      claudeRiskScore:      claudeResult.risk_score,
      claudeKeyFindings:    claudeResult.key_findings,
      claudeRecommendation: claudeResult.recommendation,
      claudeMissingMarkers: missingC,
      claudeRawResponse:    JSON.stringify(claudeResult),

      // Random Forest output
      rfRiskLevel:    rfResult.riskLevel,
      rfRiskScore:    rfResult.riskScore,
      rfConfidence:   rfResult.confidence,
      rfTreesUsed:    rfResult.treesUsed,
      rfTotalTrees:   rfResult.totalTrees,
      rfMissingKeys:  missingRf,
      rfFeatureFlagsJson: JSON.stringify(rfResult.featureFlags ?? []),

      // Action plan — stored atomically with the triage result (V6)
      actionPlanJson: serializeActionPlan(actionPlan),
    };

    const triageResponse = await saveTriageFn(patientId, record.id, triagePayload);
    safeSetStage('DONE');
    return triageResponse.data;
  }, [safeSetStage]);

  const runAutoTriage = useCallback(async (record, file, patient, patientId, preExtractedText = null, apiOverrides = {}) => {
    const saveExtractedTextFn = apiOverrides.saveExtractedText || healthRecordAPI.saveExtractedText;
    const saveTriageFn        = apiOverrides.saveTriage        || healthRecordAPI.saveTriage;
    // Server-side scan reading is only wired for the staff/patient endpoints; callers that override the API (subscriber
    // upload) must pass their own retryTriage or keep the old "scanned PDF" message.
    const retryTriageFn       = apiOverrides.retryTriage || (apiOverrides.saveExtractedText ? null : healthRecordAPI.retryTriage);

    runRecordIdRef.current = record.id;
    safeSetStage('IDLE');
    safeSetError(null);
    setIdentityMismatch(null);
    pendingRef.current = null;

    // Only auto-triage blood test PDFs — other types need different handling.
    // For re-runs (preExtractedText provided), we skip the mimeType check
    // since the file object is not available but the record is already a PDF.
    const isBloodTestPdf = record.recordType === 'BLOOD_TEST'
      && (preExtractedText || record.mimeType === 'application/pdf' || file?.type === 'application/pdf'
        || isScanImage(record.mimeType) || isScanImage(file?.type));
    const isPhoto = !preExtractedText && (isScanImage(record.mimeType) || isScanImage(file?.type));

    if (!isBloodTestPdf) {
      safeSetStage('SKIPPED');
      return { status: 'SKIPPED', triageResult: null };
    }

    try {
      // ── Step 1: Extract PDF text client-side ─────────────────────────────────
      // Skipped on re-run path — preExtractedText is already available from the DB.

      let extractedText = preExtractedText || '';

      if (!preExtractedText) {
        safeSetStage('EXTRACTING');
        try {
          extractedText = isPhoto ? '' : await extractPdfText(file);
        } catch (err) {
          // pdf.js failed — scanned PDF or corrupted.
          const msg = 'PDF text extraction failed — document may be scanned. Please contact support.';
          safeSetStage('FAILED');
          safeSetError(msg);
          return { status: 'FAILED', triageResult: null, error: msg };
        }

        if (!extractedText || extractedText.length < 50) {
          // No text layer — a scan or a photo. The server has a scan reader (OCR + second reading) and runs the whole
          // triage itself, so hand the record over rather than failing. Before this, a navigator had to notice the
          // failure and press Retry by hand.
          if (!retryTriageFn) {
            const msg = 'PDF appears to be scanned (no readable text). Please contact support.';
            safeSetStage('FAILED');
            safeSetError(msg);
            return { status: 'FAILED', triageResult: null, error: msg };
          }
          safeSetStage('SCAN_READING');
          const res = await retryTriageFn(patientId, record.id);
          const updated = res.data;
          if (updated?.processingStatus === 'TRIAGED') {
            safeSetStage('DONE');
            return { status: 'DONE', triageResult: null, record: updated };
          }
          if (updated?.processingStatus === 'AWAITING_NAVIGATOR_QC' || updated?.processingStatus === 'AWAITING_IDENTITY_CONFIRMATION') {
            // Not a failure: a person has to look at it, and the list shows why.
            safeSetStage('HELD');
            return { status: 'HELD', triageResult: null, record: updated };
          }
          const msg = updated?.triageFailureReason
            || 'This scanned report could not be read automatically. It is flagged for manual review.';
          safeSetStage('FAILED');
          safeSetError(msg);
          return { status: 'FAILED', triageResult: null, error: msg, record: updated };
        }

        // ── Step 2: PATCH extracted text to backend ─────────────────────────────
        // Only on initial upload — re-run already has the text saved.
        safeSetStage('SAVING_TEXT');
        try {
          const saved = await saveExtractedTextFn(patientId, record.id, extractedText);
          // The server may hold the report instead of accepting it for triage: a report that shows one result column per draw date is
          // split into one record per date once a navigator confirms the dates. Triaging the whole text here would mix the draws.
          if (saved?.data?.processingStatus === 'AWAITING_NAVIGATOR_QC') {
            safeSetStage('HELD');
            return { status: 'HELD', triageResult: null, record: saved.data };
          }
        } catch (err) {
          // DEF-023 follow-up: the report's own printed name/age disagrees
          // with the patient's profile. Pause here rather than failing —
          // stash what's needed to retry+resume once the caller has shown a
          // confirmation prompt and gotten a yes/no.
          if (err.response?.status === 409 && err.response?.data?.error === 'IDENTITY_MISMATCH') {
            pendingRef.current = { record, patient, patientId, extractedText, saveExtractedTextFn, saveTriageFn };
            setIdentityMismatch(err.response.data);
            safeSetStage('AWAITING_IDENTITY_CONFIRMATION');
            return { status: 'AWAITING_IDENTITY_CONFIRMATION', triageResult: null };
          }
          throw err;
        }
      } else {
        // Re-run path: text already in DB — skip Steps 1 and 2, go straight to Claude
        safeSetStage('CLAUDE');
      }

      const triageResult = await runClaudeRfAndSave(record, patient, patientId, extractedText, saveTriageFn);
      return { status: 'DONE', triageResult };

    } catch (err) {
      console.error('Auto-triage failed:', err);
      const msg = err.response?.data?.message || err.message || 'Auto-triage failed';
      safeSetStage('FAILED');
      safeSetError(msg);
      return { status: 'FAILED', triageResult: null, error: msg };
    }
  }, [runClaudeRfAndSave, safeSetStage, safeSetError]);

  /**
   * Call after the uploader confirms they want to proceed despite the
   * identity mismatch shown from `identityMismatch`. Retries the
   * extracted-text save with confirmation, then resumes the pipeline
   * exactly where it paused. Returns the same { status, triageResult } shape
   * runAutoTriage() does.
   */
  const confirmIdentityMismatch = useCallback(async (comment) => {
    const pending = pendingRef.current;
    if (!pending) return { status: 'FAILED', triageResult: null, error: 'Nothing to confirm.' };
    const { record, patient, patientId, extractedText, saveExtractedTextFn, saveTriageFn } = pending;

    try {
      safeSetStage('SAVING_TEXT');
      const saved = await saveExtractedTextFn(patientId, record.id, extractedText, true, comment);
      setIdentityMismatch(null);
      pendingRef.current = null;
      if (saved?.data?.processingStatus === 'AWAITING_NAVIGATOR_QC') {          // held for a navigator, as in runAutoTriage
        safeSetStage('HELD');
        return { status: 'HELD', triageResult: null, record: saved.data };
      }
      const triageResult = await runClaudeRfAndSave(record, patient, patientId, extractedText, saveTriageFn);
      return { status: 'DONE', triageResult };
    } catch (err) {
      console.error('Auto-triage failed after identity confirmation:', err);
      const msg = err.response?.data?.message || err.message || 'Auto-triage failed';
      safeSetStage('FAILED');
      safeSetError(msg);
      return { status: 'FAILED', triageResult: null, error: msg };
    }
  }, [runClaudeRfAndSave, safeSetStage, safeSetError]);

  /** Call when the uploader says "no, this isn't my report" — aborts the pipeline cleanly. */
  const cancelIdentityMismatch = useCallback(() => {
    pendingRef.current = null;
    setIdentityMismatch(null);
    safeSetStage('CANCELLED');
  }, [safeSetStage]);

  const reset = useCallback(() => {
    safeSetStage('IDLE');
    safeSetError(null);
    setIdentityMismatch(null);
    pendingRef.current = null;
  }, [safeSetStage, safeSetError]);

  return {
    stage,
    triageError,
    isTriaging: !['IDLE', 'DONE', 'HELD', 'FAILED', 'SKIPPED', 'AWAITING_IDENTITY_CONFIRMATION', 'CANCELLED'].includes(stage),
    identityMismatch,
    runAutoTriage,
    confirmIdentityMismatch,
    cancelIdentityMismatch,
    reset,
  };
}

